// @ts-strict-ignore
import { captureException } from '@sentry/nextjs';
import { del, get, getPublic, getServerSideConfig, post, programsGet } from 'src/utils/api';
import {
  FETCH_PROGRAM_SUCCESS, FETCH_PROGRAM_FAILURE,
  FETCH_MODULES_SUCCESS, FETCH_MODULES_FAILURE,
  ENROLL_USER_SUCCESS, ENROLL_USER_FAILURE,
  UNENROLL_USER_SUCCESS, UNENROLL_USER_FAILURE,
  FETCH_CURRENT_MODULE_SUCCESS, FETCH_CURRENT_MODULE_FAILURE,
  MARK_SECTION_AS_COMPLETE_SUCCESS, MARK_SECTION_AS_COMPLETE_FAILURE,
} from 'src/modules/programs/actionTypes';
import { setAlert } from 'src/components/alert/actions';
import { markDownParser } from 'src/utils/markDown';
import { ReduxState } from 'src/store/store';

export const fetchProgram = (programSlug: string) => {
  return async (dispatch) => {
    try {
      const response = await getPublic(`programs/${programSlug}`);
      const enrolledResponse = await getPublic(`programs/${programSlug}/total-enrolled`);
      if (response.status !== 200) {
        dispatch(setAlert());
        return dispatch({ type: FETCH_PROGRAM_FAILURE });
      }
      dispatch({
        type: FETCH_PROGRAM_SUCCESS,
        data: response.data,
        totalEnrolled: enrolledResponse.data.totalEnrolled,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: FETCH_PROGRAM_FAILURE });
    }
  };
};

const calculateProgress = (modules) => {
  const totalSections = modules.reduce((a, c) => a + c.sections.length, 0);
  const totalUncompletedSections = modules.reduce((a, c) => a + c.sections.filter(s => !s.isCompleted).length, 0);
  return Math.round(((totalSections - totalUncompletedSections) / totalSections) * 100);
};

export const fetchModules = (programSlug: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    try {
      const { currentUser, sessionId } = getState().context;
      let modules, isEnrolled;
      if (!currentUser) {
        const response = await getPublic(`programs/${programSlug}/modules`);
        if (response.status !== 200) {
          dispatch(setAlert());
          return dispatch({ type: FETCH_MODULES_FAILURE });
        }
        modules = response.data;
        isEnrolled = false;
      } else {
        const response = await get(`private/programs/${programSlug}/modules?user-id=${currentUser.userId}`, getServerSideConfig(sessionId));
        if (response.status !== 200) {
          dispatch(setAlert());
          return dispatch({ type: FETCH_MODULES_FAILURE });
        }
        modules = response.data.modules;
        // Endpoint sometimes returns isSubscribed=false after logging in from the
        // "Sign up or login to enroll" button but the user is enrolled
        isEnrolled = response.data.isSubscribed;
      }
      dispatch({
        type: FETCH_MODULES_SUCCESS,
        modules,
        progress: calculateProgress(modules),
        isEnrolled,
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: FETCH_MODULES_FAILURE });
    }
  };
};

export const enrollUser = () => {
  return async (dispatch, getState: () => ReduxState) => {
    try {
      const state = getState();
      const programSlug = state.pages.programs.details.code;
      const userId = state.context.currentUser.userId;
      const { data : { success } } = await post(`private/programs/${programSlug}/subscribe?user-id=${userId}`);
      if (!success) {
        dispatch(setAlert());
        return dispatch({ type: ENROLL_USER_FAILURE });
      }
      dispatch({ type: ENROLL_USER_SUCCESS });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: ENROLL_USER_FAILURE });
    }
  };
};

export const unenrollUser = () => {
  return async (dispatch, getState: () => ReduxState) => {
    try {
      const state = getState();
      const programSlug = state.pages.programs.details.code;
      const userId = state.context.currentUser.userId;
      const { data : { success } } = await del(`private/programs/${programSlug}/subscribe?user-id=${userId}`);
      if (!success) {
        dispatch(setAlert());
        return dispatch({ type: UNENROLL_USER_FAILURE });
      }
      dispatch({ type: UNENROLL_USER_SUCCESS });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: UNENROLL_USER_FAILURE });
    }
  };
};

export const fetchCurrentModule = (moduleCode: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    try {
      const state = getState();
      const { modules, details: { code } } = state.pages.programs;
      const currentModule = modules.find((m) => m.code === moduleCode);
      if (currentModule) {
        const response = await programsGet(`${code}/part${currentModule.sort}.md`);
        if (response.status !== 200) {
          dispatch(setAlert());
          return dispatch({ type: FETCH_CURRENT_MODULE_FAILURE });
        }
        const sectionMarkDowns = markDownParser(response.data);
        const sectionsWithMarkdown = currentModule.sections.map((section, index) => {
          return {
            programSectionId: section.programSectionId,
            title: section.title,
            ...sectionMarkDowns[index],
          };
        });
        dispatch({
          type: FETCH_CURRENT_MODULE_SUCCESS,
          currentModule: {
            name: currentModule.name,
            currentCode: moduleCode,
            sections: sectionsWithMarkdown,
            sort: currentModule.sort,
          },
          isInvalidModule: false,
        });
      } else {
        dispatch({
          type: FETCH_CURRENT_MODULE_SUCCESS,
          currentModule: null,
          // if there is no moduleCode, we show the about content
          isInvalidModule: !!moduleCode,
        });
      }
    } catch (ex) {
      captureException(ex);
      dispatch({ type: FETCH_CURRENT_MODULE_FAILURE });
    }
  };
};

export const markSectionAsComplete = (programSectionId: number, moduleCode: string) => {
  return async (dispatch, getState: () => ReduxState) => {
    try {
      const state = getState();
      const { details: { code }, modules } = state.pages.programs;
      const { data } = await post(`private/programs/${code}/mark-as-read`, { programSectionId });
      if (!data.success) {
        dispatch(setAlert());
        return dispatch({ type: MARK_SECTION_AS_COMPLETE_FAILURE });
      }
      const newModules = modules.map(m => m.code === moduleCode
        ? {
          ...m,
          sections: m.sections.map(s => s.programSectionId === programSectionId
            ? {
              ...s,
              isCompleted: true,
            }
            : s),
        }
        : m,
      );
      dispatch({
        type: MARK_SECTION_AS_COMPLETE_SUCCESS,
        newModules,
        progress: calculateProgress(newModules),
      });
    } catch (ex) {
      captureException(ex);
      dispatch({ type: MARK_SECTION_AS_COMPLETE_FAILURE });
    }
  };
};
